import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Mail, Play, Send, Undo2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import type { Listing } from "@/types";
import { DemoBadge, PageHeader } from "@/components/Doodles";

interface Status {
  ai: { spent: number; cap: number };
  season_now: string;
  schedule: { batch: string; publish: string; pins: string };
  owner_email: string;
  queue: Listing[];
  batches: { season: string; trigger: string; started_at: string; status: string; created: { id: string; name: string }[]; skipped: string[]; email?: string }[];
  pins: { listing_name: string; title: string; image_path: string; demo?: boolean; pin_id?: string; created_at: string; scheduled_for?: string; status?: string; variant?: number; rendered_at?: string }[];
  etsy_demo: boolean;
  pinterest_demo: boolean;
}

function SpendCard({ ai }: { ai: Status["ai"] }) {
  const pct = Math.min(100, (ai.spent / ai.cap) * 100);
  return (
    <div className="paper-card p-5" data-testid="ai-spend-card">
      <p className="label">AI spend this month (estimated)</p>
      <p className="font-display text-3xl font-black text-[#23324A]" data-testid="ai-spend-value">${ai.spent.toFixed(2)} <span className="text-base font-bold text-[#23324A]/60">of ${ai.cap.toFixed(2)}</span></p>
      <div className="mt-3 h-3 overflow-hidden rounded-full border-2 border-[#23324A] bg-white">
        <div className="h-full bg-[#F08A6C] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-[#23324A]/60">AI stops (copy and mockups) when the cap is reached.</p>
    </div>
  );
}

export default function AutomationPage() {
  const [s, setS] = useState<Status | null>(null);
  const [watchUntil, setWatchUntil] = useState(0);
  const load = useCallback(() => { api.get("/automation").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const running = s?.batches.some((b) => b.status === "running");
  useEffect(() => {
    if (!running && !watchUntil) { return; }
    const t = setInterval(() => {
      if (!running && Date.now() > watchUntil) { setWatchUntil(0); }
      load();
    }, 4000);
    return () => clearInterval(t);
  }, [running, watchUntil, load]);

  const call = async (url: string, ok: string) => {
    try { await api.post(url); toast.success(ok); setWatchUntil(Date.now() + 45000); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return <p className="text-sm text-[#23324A]/60">Loading…</p>;

  return (
    <div data-testid="automation-page">
      <PageHeader title="Autopilot" kicker="Weekly batch · publish queue · pins" />
      <div className="mb-10 grid gap-6 md:grid-cols-3">
        <SpendCard ai={s.ai} />
        <div className="paper-card p-5" data-testid="schedule-card">
          <p className="label flex items-center gap-2"><CalendarClock className="h-4 w-4" strokeWidth={2.5} />Schedule</p>
          <p className="text-sm text-[#23324A]"><b>Weekly batch:</b> {s.schedule.batch} ({s.season_now} ideas right now)</p>
          <p className="mt-2 text-sm text-[#23324A]"><b>Publish queue:</b> {s.schedule.publish}</p>
          <p className="mt-2 text-sm text-[#23324A]"><b>Pins:</b> {s.schedule.pins}</p>
          <p className="mt-2 flex items-center gap-2 text-sm text-[#23324A]"><Mail className="h-4 w-4" strokeWidth={2.5} />Ready email goes to {s.owner_email}</p>
        </div>
        <div className="paper-card flex flex-col gap-3 p-5">
          <button className="btn-coral justify-center" disabled={running} onClick={() => call("/automation/batch", "Batch started. You'll get an email when it's ready.")} data-testid="run-batch-btn">
            <Play className="h-4 w-4" strokeWidth={2.5} />{running ? "Batch running…" : "Run weekly batch now"}
          </button>
          <button className="btn-navy justify-center" disabled={!s.queue.length} onClick={() => call("/automation/publish", "Publishing the next 2 listings")} data-testid="publish-now-btn">
            <Send className="h-4 w-4" strokeWidth={2.5} />Publish next 2 now
          </button>
          <button className="btn-outline justify-center" onClick={() => call("/automation/pins/post-due", "Posting today's pins")} data-testid="post-pins-now-btn">Post today's pins now</button>
          {!s.queue.length && <p className="text-xs text-[#23324A]/60" data-testid="publish-hint">Nothing approved yet. Approve listings on the review screen.</p>}
          <div className="flex flex-wrap gap-2 text-xs font-semibold text-[#23324A]">
            Etsy {s.etsy_demo ? <DemoBadge /> : "live"} · Pinterest {s.pinterest_demo ? <span data-testid="pinterest-demo-badge"><DemoBadge /></span> : "live"}
          </div>
        </div>
      </div>

      <section className="mb-10">
        <h2 className="mb-3 font-display text-xl font-black text-[#23324A]">Publish queue ({s.queue.length})</h2>
        {s.queue.length === 0 ? <p className="text-sm text-[#23324A]/60" data-testid="queue-empty">Approved listings line up here, oldest first.</p> : (
          <ol className="space-y-2" data-testid="publish-queue">
            {s.queue.map((l, i) => (
              <li key={l.id} className="flex items-center gap-3 rounded-2xl border-2 border-[#23324A]/20 bg-white px-4 py-3" data-testid={`queue-item-${l.id}`}>
                <span className="font-display font-black text-[#F08A6C]">{i + 1}</span>
                <Link to={`/admin/listings/${l.id}`} className="flex-1 font-semibold text-[#23324A] hover:underline">{l.name}</Link>
                <button className="btn-ghost text-sm" onClick={() => call(`/automation/queue/${l.id}/unqueue`, "Moved back to review")} data-testid={`unqueue-${l.id}`}><Undo2 className="h-4 w-4" strokeWidth={2.5} />Back to review</button>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="mb-10">
        <h2 className="mb-3 font-display text-xl font-black text-[#23324A]">Recent batches</h2>
        {s.batches.length === 0 ? <p className="text-sm text-[#23324A]/60" data-testid="batches-empty">No batches yet. The first runs Sunday evening.</p> : (
          <ul className="space-y-2" data-testid="batches-list">
            {s.batches.map((b) => (
              <li key={b.started_at} className="rounded-2xl border-2 border-[#23324A]/20 bg-white px-4 py-3 text-sm text-[#23324A]">
                <b>{new Date(b.started_at).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" })} ET</b> · {b.season} · {b.trigger} · {b.status}
                <span className="block text-xs text-[#23324A]/70">{b.created.length} created{b.skipped.length ? `, ${b.skipped.length} skipped as duplicates` : ""}{b.email ? ` · email ${b.email}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-3 font-display text-xl font-black text-[#23324A]">Pinterest pins</h2>
        {s.pins.length === 0 ? <p className="text-sm text-[#23324A]/60" data-testid="pins-empty">Pins are posted automatically when a listing is published.</p> : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6" data-testid="pins-grid">
            {s.pins.map((p) => (
              <figure key={`${p.image_path}`} className="overflow-hidden rounded-xl border-2 border-[#23324A] bg-white" data-testid="pin-card">
                <img src={`${fileUrl(p.image_path)}?v=${p.rendered_at || ""}`} alt={p.title} className="aspect-[2/3] w-full object-cover" loading="lazy" />
                <figcaption className="p-2 text-[11px] font-semibold text-[#23324A]">{p.listing_name}
                  <span className="block text-[#23324A]/60">{p.status === "posted" ? "Posted" : "Scheduled"} {p.scheduled_for || ""}</span>
                  {p.demo && <DemoBadge />}</figcaption>
              </figure>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
